import { loadStripe } from "@stripe/stripe-js"
import posthog from "posthog-js"

import { COLORS } from "@/lib/design-tokens"
import { EMAIL_RE } from "@/lib/utils"

const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
export const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null

// The loader a checkout mounts from. It starts as `stripePromise` and moves
// to the newest attempt whenever Try again reloads Stripe's script, so a
// checkout mounted later (a promo code re-price, the create error's Try again,
// back navigation) starts from the attempt that worked, never from the first
// one that failed. `stripePromise` itself stays the "is a key configured" test.
let currentStripeLoader = stripePromise

/** The Stripe loader a checkout should mount from right now. */
export function stripeLoader() {
  return currentStripeLoader
}

/** A fresh attempt at Stripe's script after it failed to load (a content
 *  blocker, a network filter, a dropped connection). `loadStripe` clears its
 *  own cache when the script fails, so calling it again injects the script
 *  again rather than handing back the same rejected promise. */
export function reloadStripe() {
  currentStripeLoader = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null
  return currentStripeLoader
}

/** Where the payment form failed: Stripe's script never arrived, or it arrived
 *  and the Payment Element reported a load error. */
export type PaymentFormLoadStage = "stripe-js" | "payment-element"

/** Tells the owner a buyer's payment form did not load
 *  (/api/stripe/embedded-checkout/load-failure, which proves possession of the
 *  intent's client secret and alerts once per intent). Best effort: the buyer
 *  already sees the message and a retry, so a failed report never blocks him. */
export async function reportPaymentFormLoadFailure(input: {
  paymentIntentId: string
  clientSecret: string
  stage: PaymentFormLoadStage
  detail: string
}): Promise<void> {
  let sessionId: string | undefined
  try {
    sessionId = posthog.get_session_id() || undefined
  } catch {
    sessionId = undefined
  }
  try {
    await fetch("/api/stripe/embedded-checkout/load-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...input,
        sessionId,
        page: `${window.location.pathname}${window.location.search}`,
      }),
    })
  } catch {
    // The page already shows the buyer what happened; nothing else to do here.
  }
}

// PayPal is a fully additive second payment option next to Stripe (owner-
// approved research; context/payments-system.md § PayPal).
// PayPal's client id is not a secret (same posture as Stripe's publishable
// key) — it's read from a NEXT_PUBLIC_ var so the button can load client-side.
// Ships inert: with no client id set, every PayPal-specific branch below just
// renders nothing, and the Stripe flow is completely unaffected.
export const PAYPAL_CLIENT_ID = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID

export const ELEMENTS_APPEARANCE = {
  theme: "stripe" as const,
  variables: {
    colorPrimary: COLORS.vermilion,
    colorBackground: COLORS.paper,
    colorText: COLORS.ink,
    colorTextSecondary: COLORS.inkMuted,
    colorDanger: COLORS.vermilion,
    borderRadius: "10px",
    fontSizeBase: "15px",
  },
}

// PayPal is never in the payment-method order, since it renders as its own
// button after the Payment Element, at every price.
//
// Pay-over-time rails, all three verified ACTIVE on the live account
// (affirm_payments, klarna_payments, afterpay_clearpay_payments). They were
// already rendering, but only wherever Stripe's own default order happened to
// put them -- which is below the fold on a phone, so a buyer who could have
// split the payment never saw that he could. Naming them here lifts them to a
// fixed position directly under the leading method.
//
// This is ORDER, not a restriction: a method Stripe cannot offer for a given
// amount or country is simply skipped (each rail has its own price band), and
// nothing here hard-codes payment_method_types, which the account gate forbids.
const PAY_OVER_TIME = ["klarna", "afterpay_clearpay", "affirm"]

/** Bank, then card, then the pay-over-time rails, in that order at EVERY
 *  amount. The amount is taken and deliberately ignored: this stays the one
 *  function a caller reaches for, so a price band cannot creep back in at a
 *  call site, and an eval can pin the order either side of the old $900 line. */
export function paymentMethodOrderFor(_amountCents: number): string[] {
  return ["us_bank_account", "card", ...PAY_OVER_TIME]
}

export function isLikelyEmail(value: string) {
  return EMAIL_RE.test(value.trim())
}

// Matches the JSON contract /api/stripe/embedded-checkout returns
// (web/lib/billing/checkout-copy.ts CheckoutOfferCopy) — defined locally so
// this client page never imports from web/lib/billing at runtime.
export type OfferCopy = {
  title: string
  subtitle: string
  included: string[]
  /** Owner checkout spec (2026-08-20) — see CheckoutOfferCopy in
   *  web/lib/billing/checkout-copy.ts for what each field carries. */
  confirmHeading?: string
  confirmLine?: string
  payVerbLabel?: string
  scheduleNote?: string
}

/** The typed hand-out-code affordance ("Have a promo code?"). Applying a code
 *  re-prices the order by creating a fresh PaymentIntent server-side
 *  (web/lib/billing/checkout.ts resolveHandoutCoupon); this is UI-only state. */
export type PromoProps = {
  onApply: (code: string) => void
  applying: boolean
  /** One plain error line for an unknown/inactive code — never a discount
   *  breakdown, a percentage, or the code itself. */
  error: string
  applied: boolean
}

/** What the standalone PayPal checkout will charge, resolved on the server by
 *  the same price source POST /api/paypal/create-order uses
 *  (web/lib/billing/paypal-standalone-price.ts), so the order summary the
 *  buyer reads is exactly what PayPal is asked for. Null for anything PayPal
 *  cannot sell alone (a recurring, retired or unknown plan, or a price or
 *  amount link). */
export type PayPalStandaloneOffer = {
  planId: string
  offer: OfferCopy
  amountCents: number
  currency: string
}

/** A weekly plan opened from a prepaid block's renewal reminder: nothing is
 *  charged today and the first weekly charge lands the day the block ends.
 *  Null on every other checkout. */
export type DeferredStart = {
  /** ISO time of the first weekly charge. */
  firstChargeAt: string
  /** The weekly price, in cents. */
  recurringAmount: number
}

export type SessionState =
  | { status: "loading" }
  /** `needsSignIn`: the route answered 401 (an account-granting plan, signed
   *  out), so the way forward is signing in, not a retry. */
  | { status: "error"; message: string; needsSignIn?: boolean; httpStatus?: number }
  /** A RECURRING plan cannot open a Stripe subscription without a customer, and
   * a customer needs an email a signed-out buyer has not given us yet. So the
   * weekly tiers ask for it first and create the subscription on submit. A
   * signed-out COURSE purchase asks the same way, because the webhook mints the
   * buyer's account from that email, and so does a signed-out ARTICLE
   * subscription, for the same reason. Unreachable for every other one-time
   * offer. */
  | {
      status: "needs-email"
      offer: OfferCopy
      amountCents: number
      currency: string
      recurringInterval: string | null
      prefillEmail: string | null
      deferredStart: DeferredStart | null
      /** The webhook makes his account from this address once he pays, so the
       *  field's hint says so even on a recurring plan. */
      createsAccount: boolean
    }
  | {
      status: "ready"
      clientSecret: string
      /** pi_… for a checkout that charges today; seti_… for one that does not. */
      paymentIntentId: string
      /** "setup" when nothing is charged today, so the form confirms the
       *  subscription's SetupIntent (confirmSetup) instead of a payment. */
      intentKind: "payment" | "setup"
      deferredStart: DeferredStart | null
      offer: OfferCopy
      amountCents: number
      currency: string
      /** The buyer's email when we already have it (signed in, or typed at
       * the email step of a signed-out course or recurring checkout), so the
       * contact-information block prefills instead of asking again. */
      prefillEmail: string | null
      /** "a week" on a recurring tier, null on a one-time offer. Drives the
       * cadence on the total and the pay button, and switches off the promo
       * field and PayPal, neither of which can reach a recurring invoice. */
      recurringInterval: string | null
      /** A signed-out course purchase. PayPal is off for it: the PayPal
       * capture grants to metadata.userId and has no account to grant to. */
      guestCheckout: boolean
    }

/** Stamps the clickwrap policy-agreement timestamp onto the PaymentIntent,
 * retrying transient failures before giving up. This is best-effort
 * recordkeeping — the actual gate is the disabled Pay button below, which
 * never calls confirmPayment unless the checkbox is ticked — but it's also
 * the ONLY server-side audit trail of that agreement for the embedded flow
 * (a raw PaymentIntent has no Checkout-Session-style consent_collection), so
 * a swallowed failure here is worth surfacing, not silently dropping. A
 * webhook-side fallback stamp (web/app/api/stripe/webhook/route.ts,
 * payment_intent.succeeded) covers the case where every retry here still
 * fails. */
export async function stampPolicyAgreementWithRetry(
  paymentIntentId: string,
  clientSecret: string,
): Promise<boolean> {
  const attempts = 3
  let lastError: unknown

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const res = await fetch("/api/stripe/embedded-checkout/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentIntentId,
          clientSecret,
        }),
      })
      if (res.ok) return true
      lastError = new Error(`Consent stamp responded ${res.status}`)
    } catch (err) {
      lastError = err
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)))
    }
  }

  // Every attempt failed — this is the one place a policy-agreement stamp can
  // silently go missing, so make it visible instead of swallowing it.
  posthog.captureException(lastError instanceof Error ? lastError : new Error(String(lastError)), {
    route: "/pay/embedded",
    context: "policy-agreement-stamp-failed",
    paymentIntentId,
  })
  return false
}

/** The line under "Total due today" on a recurring offer, following the
 *  offer's own cadence. `interval` is planIntervalLabel()'s wording (web/lib/
 *  billing/plans.ts). The weekly line is the one the Mentorship tiers have
 *  always shown, word for word. The yearly line states only when the next
 *  charge lands: no cancel or refund term is set for the yearly price, and his
 *  picked yearly row says only "$490/year · Billed yearly". */
export function recurringChargeLine(amount: string, interval: string): string {
  switch (interval) {
    case "a week":
      return `Then ${amount} a week, starting next week. Cancel any week by email.`
    case "a month":
      return `Then ${amount} a month, starting next month. Cancel anytime by email.`
    case "a year":
      return `Then ${amount} a year, starting next year.`
    default:
      return `Then ${amount} ${interval}.`
  }
}

/** "Thursday, October 29 Eastern", in Eastern time, the zone every date in the
 *  buyer's emails is written in. */
export function easternLongDate(iso: string): string {
  const day = new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "America/New_York",
  })
  return `${day} Eastern`
}

/** The line under "Total due today" on a weekly plan that starts when his
 *  prepaid block ends. The weekly line above, with the start date in place of
 *  "next week", because that date is the whole point of this checkout. */
export function deferredChargeLine(amount: string, firstChargeAt: string): string {
  return `Then ${amount} a week, starting ${easternLongDate(firstChargeAt)}, the day your block ends. Cancel any week by email.`
}

/** The `?renews=<block id>` a prepaid Mentorship block's renewal reminder puts
 *  on its links (the block links and the weekly one), passed through to
 *  /api/stripe/embedded-checkout, which alone decides what it may do.
 *  Undefined when absent. */
export function renewsQueryFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    const value = new URLSearchParams(window.location.search).get("renews")
    return value?.trim() ? value.trim().slice(0, 64) : undefined
  } catch {
    return undefined
  }
}

/** The `?weekend=<Friday ISO date>` a "Take this weekend" link on /bootcamp
 *  puts on its Group Bootcamp checkout, passed through to
 *  /api/stripe/embedded-checkout, which alone decides whether it names a
 *  published weekend (web/lib/billing/bootcamp-weekends.ts). Undefined when
 *  absent. */
export function weekendQueryFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    const value = new URLSearchParams(window.location.search).get("weekend")
    return value?.trim() ? value.trim().slice(0, 10) : undefined
  } catch {
    return undefined
  }
}

export function formatAmount(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100)
  } catch {
    return `$${(cents / 100).toLocaleString("en-US")}`
  }
}
